import {
    AgencyArtifactResolutionType,
    AgencySpecReferenceFormat,
} from './types';

export interface AgencyAgentReference {
    name: string;
    type: AgencyArtifactResolutionType;
    pluginName?: string;
    pluginArtifactName?: string;
}

export interface AgencyPluginReference {
    name: string;
    type: AgencyArtifactResolutionType;
}

export interface ParsedAgencySpecReference {
    format: AgencySpecReferenceFormat;
    segments: string[];
}

export function parseAgencyPluginArtifact(
    artifactName: string,
    artifactText?: string | null,
): AgencyPluginReference {
    if (!artifactText?.trim()) {
        return {
            name: artifactName.trim(),
            type: 'Company',
        };
    }

    try {
        const parsed = JSON.parse(artifactText) as Partial<AgencyPluginReference>;
        return {
            name: parsed.name?.trim() || artifactName.trim(),
            type: parsed.type ?? 'Company',
        };
    } catch {
        return {
            name: artifactName.trim(),
            type: 'Company',
        };
    }
}

export function normalizeSpecSegments(format: AgencySpecReferenceFormat, segments?: string[] | null): string[] {
    const expectedLength = format === 'market' ? 3 : format === 'ado-git' ? 5 : 4;
    return Array.from({ length: expectedLength }, (_, index) => {
        const value = segments?.[index] ?? '';
        const isPath = (format === 'ado-git' && index === 3) || (format === 'github' && index === 2);
        return isPath ? value.replace(/^\/+/, '') : value;
    });
}

export function parseSpecEncodedReference(name?: string | null): ParsedAgencySpecReference | null {
    if (!name?.trim()) {
        return null;
    }

    const trimmedName = name.trim();
    if (trimmedName.toLowerCase().startsWith('market:')) {
        const remainder = trimmedName.slice('market:'.length);
        const hashIndex = remainder.indexOf('#');
        const mainPart = hashIndex >= 0 ? remainder.slice(0, hashIndex) : remainder;
        const ref = hashIndex >= 0 ? remainder.slice(hashIndex + 1) : '';
        const atIndex = mainPart.indexOf('@');

        return {
            format: 'market',
            segments: normalizeSpecSegments('market', [
                atIndex >= 0 ? mainPart.slice(0, atIndex) : mainPart,
                atIndex >= 0 ? mainPart.slice(atIndex + 1) : '',
                ref,
            ]),
        };
    }

    if (trimmedName.toLowerCase().startsWith('ado-git:')) {
        const remainder = trimmedName.slice('ado-git:'.length);
        const atIndex = remainder.indexOf('@');
        const mainPart = atIndex >= 0 ? remainder.slice(0, atIndex) : remainder;
        const ref = atIndex >= 0 ? remainder.slice(atIndex + 1) : '';
        const colonIndex = mainPart.indexOf(':');
        const repoPart = colonIndex >= 0 ? mainPart.slice(0, colonIndex) : mainPart;
        const path = colonIndex >= 0 ? mainPart.slice(colonIndex + 1) : '';
        const repoSegments = repoPart.split('/');

        return {
            format: 'ado-git',
            segments: normalizeSpecSegments('ado-git', [
                repoSegments[0] ?? '',
                repoSegments[1] ?? '',
                repoSegments.slice(2).join('/'),
                path,
                ref,
            ]),
        };
    }

    if (trimmedName.toLowerCase().startsWith('github:')) {
        const remainder = trimmedName.slice('github:'.length);
        const atIndex = remainder.indexOf('@');
        const mainPart = atIndex >= 0 ? remainder.slice(0, atIndex) : remainder;
        const ref = atIndex >= 0 ? remainder.slice(atIndex + 1) : '';
        const colonIndex = mainPart.indexOf(':');
        const repoPart = colonIndex >= 0 ? mainPart.slice(0, colonIndex) : mainPart;
        const path = colonIndex >= 0 ? mainPart.slice(colonIndex + 1) : '';
        const repoSegments = repoPart.split('/');

        return {
            format: 'github',
            segments: normalizeSpecSegments('github', [
                repoSegments[0] ?? '',
                repoSegments.slice(1).join('/'),
                path,
                ref,
            ]),
        };
    }

    return null;
}

export function buildSpecEncodedReference(format: AgencySpecReferenceFormat, segments: string[]): string {
    const normalized = normalizeSpecSegments(format, segments).map(segment => segment.trim());
    if (format === 'market') {
        const [name, location, ref] = normalized;
        const mainPart = location ? `${name}@${location}` : name;
        return ref ? `market:${mainPart}#${ref}` : `market:${mainPart}`;
    }

    if (format === 'ado-git') {
        const [organization, project, repository, path, ref] = normalized;
        const mainPart = [organization, project, repository].filter(Boolean).join('/');
        const withPath = path ? `${mainPart}:${path}` : mainPart;
        return ref ? `ado-git:${withPath}@${ref}` : `ado-git:${withPath}`;
    }

    const [owner, repository, path, ref] = normalized;
    const mainPart = [owner, repository].filter(Boolean).join('/');
    const withPath = path ? `${mainPart}:${path}` : mainPart;
    return ref ? `github:${withPath}@${ref}` : `github:${withPath}`;
}

export function buildBundledAgentReference(
    agentName: string,
    plugin: AgencyPluginReference,
): AgencyAgentReference {
    const normalizedAgentName = agentName.trim();
    if (plugin.type !== 'Spec') {
        return {
            name: normalizedAgentName,
            type: plugin.type,
            pluginName: plugin.name.trim(),
        };
    }

    const parsedPlugin = parseSpecEncodedReference(plugin.name);
    if (!parsedPlugin) {
        throw new Error(`Plugin '${plugin.name}' is not a supported Agency spec reference.`);
    }

    if (parsedPlugin.format === 'market') {
        const [pluginName, location, ref] = parsedPlugin.segments;
        return {
            name: buildSpecEncodedReference('market', [`${pluginName}:${normalizedAgentName}`, location, ref]),
            type: 'Spec',
        };
    }

    const pathIndex = parsedPlugin.format === 'ado-git' ? 3 : 2;
    const nextSegments = [...parsedPlugin.segments];
    const pluginPath = nextSegments[pathIndex].replace(/\/+$/, '');
    nextSegments[pathIndex] = `${pluginPath}/agents/${normalizedAgentName}`;
    return {
        name: buildSpecEncodedReference(parsedPlugin.format, nextSegments),
        type: 'Spec',
    };
}

export function buildStoredBundledAgentReference(
    agentName: string,
    pluginArtifactName: string,
    plugin: AgencyPluginReference,
): AgencyAgentReference {
    return {
        name: agentName.trim(),
        type: plugin.type,
        pluginArtifactName: pluginArtifactName.trim(),
    };
}

export function derivePluginFromBundledAgent(agent: AgencyAgentReference): {
    agentName: string;
    plugin: AgencyPluginReference;
} | null {
    if (agent.type !== 'Spec') {
        if (!agent.pluginName?.trim()) {
            return null;
        }

        return {
            agentName: agent.name,
            plugin: {
                name: agent.pluginName.trim(),
                type: agent.type,
            },
        };
    }

    const parsedAgent = parseSpecEncodedReference(agent.name);
    if (!parsedAgent) {
        return null;
    }

    if (parsedAgent.format === 'market') {
        const [bundledName, location, ref] = parsedAgent.segments;
        const separatorIndex = bundledName.lastIndexOf(':');
        if (separatorIndex <= 0 || separatorIndex === bundledName.length - 1) {
            return null;
        }

        return {
            agentName: bundledName.slice(separatorIndex + 1),
            plugin: {
                name: buildSpecEncodedReference('market', [bundledName.slice(0, separatorIndex), location, ref]),
                type: 'Spec',
            },
        };
    }

    const pathIndex = parsedAgent.format === 'ado-git' ? 3 : 2;
    const agentPath = parsedAgent.segments[pathIndex];
    const separatorIndex = agentPath.lastIndexOf('/agents/');
    if (separatorIndex <= 0 || separatorIndex + '/agents/'.length === agentPath.length) {
        return null;
    }

    const pluginSegments = [...parsedAgent.segments];
    pluginSegments[pathIndex] = agentPath.slice(0, separatorIndex);
    return {
        agentName: agentPath.slice(separatorIndex + '/agents/'.length),
        plugin: {
            name: buildSpecEncodedReference(parsedAgent.format, pluginSegments),
            type: 'Spec',
        },
    };
}

export function agencyReferencesEqual(left: AgencyPluginReference, right: AgencyPluginReference): boolean {
    return left.type === right.type
        && left.name.trim().localeCompare(right.name.trim(), undefined, { sensitivity: 'accent' }) === 0;
}
